import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { GridRenderEditCellParams, useGridApiContext } from '@mui/x-data-grid';
import CircularProgress from '@mui/material/CircularProgress';

interface ActivityOption {
  id: number | null;
  value: string;
  label: string;
}

interface ActivitySelectEditorProps extends GridRenderEditCellParams {
  valueOptions: ActivityOption[];
  onSearchChange: (searchTerm: string) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

export function ActivitySelectEditor({
  id,
  field,
  value,
  valueOptions,
  onSearchChange,
  searchTerm,
  setSearchTerm
}: ActivitySelectEditorProps) {
  const apiRef = useGridApiContext();
  const [loading, setLoading] = React.useState(false);

  // Find the value in options
  const selectedOption = valueOptions.find(option => option.value === value) || null;

  // Handle input change for search
  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input') {
      setSearchTerm(newInputValue);

      // Set loading state
      setLoading(true);

      // Debounce search to avoid too many API calls
      const timeoutId = setTimeout(() => {
        onSearchChange(newInputValue);
        setLoading(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  };

  // Handle value selection
  const handleValueChange = (event, newValue: ActivityOption | null) => {
    // Update the cell value
    apiRef.current.setEditCellValue({
      id,
      field,
      value: newValue?.value || ''
    });

    // Reset search term after selection
    setSearchTerm('');
  };

  return (
    <Autocomplete
      fullWidth
      autoHighlight
      disableClearable={false}
      options={valueOptions}
      value={selectedOption}
      onChange={handleValueChange}
      inputValue={searchTerm}
      onInputChange={handleInputChange}
      loading={loading}
      loadingText="Searching..."
      isOptionEqualToValue={(option, value) => {
        if (!option && !value) return true;
        if (!option || !value) return false;
        return option.value === value.value;
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          placeholder="Search..."
          sx={{ padding: '2px' }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default ActivitySelectEditor;